import React from "react";
import { Flex } from "rebass/styled-components";
import styled from "styled-components";

import { Title2, Body, Subheader } from "MVVM/Components/Text";
import { GameLogos } from "MVVM/Components/Icons";
import { ESupportedGames } from "MVVM/@types";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import { etaFromIso } from "MVVM/Utilities/approxTimeDisplay";
import { useUser } from "MVVM/Hooks/useUser";
import { Stepper } from "MVVM/Components/ClipStatus/Components/TrackerCard/Stepper";
import type { ClipStatusTimeline } from "MVVM/Components/ClipStatus/@types";
import {
  buildSupportLink,
  getCapturingFill,
  getSegmentDuration,
  getStatusCopy,
  isTimelineErrored,
  isTimelineStale,
  resolveActiveIndex,
} from "MVVM/Components/ClipStatus/timelineDisplay";

type ClipPendingProps = {
  title?: string;
  /** EGameKey string off the clip, e.g. "CS2". */
  game?: string;
  timeline?: ClipStatusTimeline;
};

export const ClipPending = ({ title, game, timeline }: ClipPendingProps) => {
  const { allstarUser } = useUser();

  // Pulse carries the numeric app id; the clip carries an EGameKey string.
  const gameLogo =
    (timeline?.game as ESupportedGames | undefined) ??
    getMatchHistoryGameEnum(game);

  const activeIdx = timeline ? resolveActiveIndex(timeline.events) : -1;
  const isError = !!timeline && isTimelineErrored(timeline);
  const isStale = !!timeline && isTimelineStale(timeline);
  const estimatedCompletionAt = timeline?.desktop?.estimatedCompletionAt;
  const eta = estimatedCompletionAt ? etaFromIso(estimatedCompletionAt) : null;

  return (
    <PendingBox flexDirection="column">
      <HeaderRow>
        <HeaderLeft>
          {gameLogo != null && gameLogo !== ESupportedGames.NONE && (
            <GameLogos
              type={gameLogo}
              size={["20", "24"]}
              styles={{ marginRight: 0 }}
            />
          )}
          <PendingTitle>{title || "Your clip"}</PendingTitle>
        </HeaderLeft>
        {eta && !isError && (
          <EtaText color={isStale ? "caution" : "envy"}>{eta}</EtaText>
        )}
      </HeaderRow>

      {timeline ? (
        <Stepper
          activeIdx={activeIdx}
          isError={isError}
          fillDuration={getSegmentDuration(activeIdx, estimatedCompletionAt)}
          activeFill={getCapturingFill(
            activeIdx,
            timeline.events,
            estimatedCompletionAt,
          )}
        />
      ) : (
        <SpinnerRow>
          <LoadingSpinner width={40} height={40} />
        </SpinnerRow>
      )}

      <StatusText color={isError ? "alert" : isStale ? "caution" : "ice"}>
        {timeline ? getStatusCopy(timeline) : "Processing your clip"}
      </StatusText>

      <HintText color="chalk">
        {isError
          ? "This one didn't make it. Reach out and we'll take a look."
          : "Hang tight, we'll play it here as soon as it's ready."}
      </HintText>

      {timeline && (isError || isStale) && (
        <SupportLink
          href={buildSupportLink(timeline, allstarUser?.user?._id)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Get help
        </SupportLink>
      )}
    </PendingBox>
  );
};

const PendingBox = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  gap: 8px;
  max-width: 480px;
  padding: 24px;
  width: 100%;
  /* The card sits inside the page's backdrop-filter. Its own layer plus paint
     containment keeps the stepper from re-blurring the backdrop every frame. */
  contain: paint;
  isolation: isolate;
  transform: translateZ(0);

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    padding: 16px;
  }
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`;

const HeaderLeft = styled(Flex)`
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const PendingTitle = styled(Title2)`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EtaText = styled(Body)`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
`;

const SpinnerRow = styled(Flex)`
  align-items: center;
  justify-content: center;
  padding: 8px 0;
`;

const StatusText = styled(Subheader)`
  font-weight: 600;
`;

const HintText = styled(Subheader)`
  font-weight: 500;
`;

const SupportLink = styled.a`
  align-self: flex-start;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 500;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.envy};
  text-underline-offset: 2px;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;
